// AudioDetector — future responsibility: listen to the guitar and emit a
// PlayerState. Build 1 ships a MockAudioDetector (no real pitch detection)
// plus a lightweight, clearly-experimental microphone LEVEL reader used only
// for input visualization (no fake AI analysis).

import { PlayerState } from "@/models/types";

export interface AudioDetector {
  start(onState: (state: PlayerState) => void): void;
  stop(): void;
}

/** Emits gently varying simulated confidence values. Never claims real detection. */
export class MockAudioDetector implements AudioDetector {
  private timer: number | null = null;

  start(onState: (state: PlayerState) => void): void {
    this.stop();
    this.timer = window.setInterval(() => {
      const jitter = () => 0.55 + Math.random() * 0.4;
      onState({
        isPlaying: true,
        detectedNote: null, // Build 1 does not detect real notes
        pitchConfidence: jitter(),
        rhythmConfidence: jitter(),
        harmonicConfidence: jitter(),
        flowConfidence: jitter(),
        needsRecovery: false,
      });
    }, 500);
  }

  stop(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }
}

/** Experimental: real microphone amplitude for the input visualizer only. */
export class MicLevelReader {
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private stream: MediaStream | null = null;
  private data: Uint8Array | null = null;

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const src = this.ctx.createMediaStreamSource(this.stream);
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 512;
    src.connect(this.analyser);
    this.data = new Uint8Array(this.analyser.frequencyBinCount);
  }

  /** Returns a 0..1 RMS-ish level. */
  level(): number {
    if (!this.analyser || !this.data) return 0;
    this.analyser.getByteTimeDomainData(this.data);
    let sum = 0;
    for (let i = 0; i < this.data.length; i++) {
      const v = (this.data[i] - 128) / 128;
      sum += v * v;
    }
    return Math.min(1, Math.sqrt(sum / this.data.length) * 3.2);
  }

  stop(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.ctx?.close();
    this.ctx = null;
    this.analyser = null;
    this.stream = null;
    this.data = null;
  }
}
